import { ID, Query, type Models } from "appwrite";
import { databases } from "@/lib/appwrite"; // ajusta a tu cliente
import type { Holiday } from "@/lib/collections/holidays/types";

const DATABASE_ID = import.meta.env.VITE_APPWRITE_DATABASE_ID; // ajusta a tu variable
const HOLIDAYS_COLLECTION_ID = import.meta.env.VITE_HOLIDAYS_COLLECTION_ID;

type HolidayDoc = Models.Document & Omit<Holiday, "$id">;

const toHoliday = (document: HolidayDoc): Holiday => ({
    $id: document.$id,
    date: document.date,
    name: document.name,
    description: document.description ?? null,
    canton: document.canton ?? null,
    year: document.year,
    recoverable: document.recoverable ?? false,
});

// Recibe una lista de años porque unas vacaciones pueden cruzar de diciembre a enero
export async function getHolidays(years: number[]): Promise<Holiday[]> {
    const res = await databases.listDocuments<HolidayDoc>(DATABASE_ID, HOLIDAYS_COLLECTION_ID, [
        Query.equal("year", years),
        Query.orderAsc("date"),
        Query.limit(200),
    ]);
    return res.documents.map(toHoliday);
}

export type NewHoliday = Omit<Holiday, "$id" | "year">;

// Duplicado = misma fecha y mismo cantón (o ambos nacionales). Al editar se excluye el propio documento.
async function assertNoDuplicate(date: string, canton: string | null, excludeId?: string) {
    const existing = await databases.listDocuments<HolidayDoc>(DATABASE_ID, HOLIDAYS_COLLECTION_ID, [
        Query.equal("date", date),
        Query.limit(50),
    ]);
    const duplicate = existing.documents.some(
        (d) => d.$id !== excludeId && (d.canton ?? null) === canton
    );
    if (duplicate) throw new Error("DUPLICATE");
}

const buildData = (input: NewHoliday) => ({
    date: input.date,
    name: input.name.trim(),
    description: input.description?.trim() || null,
    canton: input.canton || null,
    year: Number(input.date.slice(0, 4)),
    recoverable: input.recoverable ?? false,
});

export async function createHoliday(input: NewHoliday): Promise<Holiday> {
    const data = buildData(input);
    await assertNoDuplicate(data.date, data.canton);
    const doc = await databases.createDocument<HolidayDoc>(
        DATABASE_ID,
        HOLIDAYS_COLLECTION_ID,
        ID.unique(),
        data
    );
    return toHoliday(doc);
}

export async function updateHoliday(id: string, input: NewHoliday): Promise<Holiday> {
    const data = buildData(input);
    await assertNoDuplicate(data.date, data.canton, id);
    const doc = await databases.updateDocument<HolidayDoc>(
        DATABASE_ID,
        HOLIDAYS_COLLECTION_ID,
        id,
        data
    );
    return toHoliday(doc);
}

export async function deleteHoliday(id: string): Promise<void> {
    await databases.deleteDocument(DATABASE_ID, HOLIDAYS_COLLECTION_ID, id);
}